import type { MutableRefObject } from "react";
import { failure, ok, pending, Result } from "@/common/common";
import {
  DCEvent,
  EventDispatcher,
  showGeneralError,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import {
  CardAction,
  cardActions,
  DashboardCardConfig,
  DEFAULT_COLUMN_SPAN,
  GRID_COLUMNS,
  DashboardCardData,
  DashboardCardDataRequest,
  DashboardGroup,
  dashboardApiClient,
  TemplateCardAction,
  templateCardActions,
} from "./dashboardApiClient";
import { templateApiClient } from "@/templates/templateApiClient";
import {
  dashboardGroupLabel,
  DashboardSettingsState,
  PreviewCompany,
  updateDashboardSettings,
} from "./dashboardSettingsDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { LinkButtonProps } from "@/components-v2/Button";
import { IconProps } from "@/components-v2/Icon";
import { GraphUtils } from "@/components-v2/DataGraph/dataGraphDomain";
import { downloadFile } from "@/api/api";
import { logError } from "@/utils/log";

export type PreviewEntry = { key: string; config: DashboardCardConfig };

export const draftCardKey = "draft";

export const dialogPreviewKey = (cardId: string): string => `${draftCardKey}:${cardId}`;

const setPreviewData = (
  accountId: string,
  key: string,
  result: Result<DashboardCardData>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) =>
      state?.accountId === accountId
        ? { ...state, previewData: { ...state.previewData, [key]: result } }
        : state,
  },
});

export type CardCompany = { orgNo: string; companyId: string };

export const loadCardData = (
  company: CardCompany,
  entries: PreviewEntry[],
  request: Omit<DashboardCardDataRequest, "cards">,
  setResult: (key: string, result: Result<DashboardCardData>) => UpdateStoreEvent,
): DCEvent[] => [
  ...loadDataCards(
    company.orgNo,
    entries.filter(({ config }) => loadsCardData(config)),
    request,
    setResult,
  ),
  ...entries.flatMap(({ key, config }) =>
    config.type === "TEMPLATE" && config.templateId
      ? loadTemplateCard(company.companyId, key, config.templateId, setResult)
      : [],
  ),
];

const loadDataCards = (
  orgNo: string,
  entries: PreviewEntry[],
  request: Omit<DashboardCardDataRequest, "cards">,
  setResult: (key: string, result: Result<DashboardCardData>) => UpdateStoreEvent,
): DCEvent[] =>
  entries.length === 0
    ? []
    : [
        ...entries.map(({ key }) => setResult(key, pending())),
        dashboardApiClient.cardData(
          orgNo,
          { cards: entries, ...request },
          {
            onSuccess: (res) =>
              entries.map(({ key }) => {
                const data = res.data.cards[key];
                return setResult(key, data ? ok(data) : failure());
              }),
            onFailure: () => entries.map(({ key }) => setResult(key, failure())),
          },
        ),
      ];

const loadTemplateCard = (
  companyId: string,
  key: string,
  templateId: string,
  setResult: (key: string, result: Result<DashboardCardData>) => UpdateStoreEvent,
): DCEvent[] => [
  setResult(key, pending()),
  templateApiClient.getContent(templateId, companyId, {
    onSuccess: (res) => [setResult(key, ok({ type: "TEMPLATE", content: res.data }))],
    onFailure: () => [setResult(key, failure())],
  }),
];

export const loadPreviewData = (
  accountId: string,
  company: CardCompany | undefined,
  entries: PreviewEntry[],
  request: Omit<DashboardCardDataRequest, "cards">,
): DCEvent[] =>
  company
    ? loadCardData(company, entries, request, (key, result) =>
        setPreviewData(accountId, key, result),
      )
    : [];

export const previewCompany = (state: DashboardSettingsState): PreviewCompany | undefined =>
  state.companies.find((company) => company.orgNo === state.previewOrgNo);

export const previewGroup = (
  state: DashboardSettingsState,
  visibleTo: string[] = [],
): DashboardGroup | undefined => {
  const groups = state.overview?.groups ?? [];
  return (
    groups.find((group) => group.grantee === state.previewGroup) ??
    groups.find((group) => visibleTo.includes(group.grantee)) ??
    groups.find((group) => group.type === "ACCOUNT_ADMINS")
  );
};

export const previewNote = (
  group: DashboardGroup | undefined,
  visibleTo: string[],
  isEnabled: boolean,
): TMessage | undefined =>
  !isEnabled
    ? { key: "enterprise-account.dashboards.preview-disabled" }
    : group && !visibleTo.includes(group.grantee)
      ? {
          key: "enterprise-account.dashboards.preview-not-visible",
          params: { group: dashboardGroupLabel(group) },
        }
      : undefined;

export const setPreviewGroup = (state: DashboardSettingsState, grantee: string): DCEvent[] => [
  updateDashboardSettings(state.accountId, { previewGroup: grantee, previewData: {} }),
];

export const setPreviewCompany = (state: DashboardSettingsState, orgNo: string): DCEvent[] => [
  updateDashboardSettings(state.accountId, { previewOrgNo: orgNo, previewData: {} }),
];

export const MAP_CARD_HEIGHT = 250;

export const cardWidth = (config: DashboardCardConfig): string =>
  `${(cardColumnSpan(config) / GRID_COLUMNS) * 100}%`;

export const cardColumnSpan = (config: DashboardCardConfig): number => {
  switch (config.type) {
    case "ENTITY_DATA":
    case "COMPANY_ROLES":
    case "ROLE_HOLDER":
      return DEFAULT_COLUMN_SPAN;
    case "TEMPLATE":
      return config.columnSpan ?? DEFAULT_COLUMN_SPAN;
  }
};

export type CardSkeletonShape = { type: "rows"; count: number } | { type: "block"; height: number };

export const cardSkeleton = (config: DashboardCardConfig): CardSkeletonShape => {
  switch (config.type) {
    case "COMPANY_ROLES":
      return { type: "rows", count: Math.max(1, config.roles.length) };
    case "ENTITY_DATA":
    case "ROLE_HOLDER":
      return { type: "rows", count: Math.max(1, config.fields.length) };
    case "TEMPLATE":
      return { type: "block", height: MAP_CARD_HEIGHT };
  }
};

export const loadsCardData = (config: DashboardCardConfig): boolean => {
  switch (config.type) {
    case "ENTITY_DATA":
    case "COMPANY_ROLES":
    case "ROLE_HOLDER":
      return true;
    case "TEMPLATE":
      return false;
  }
};

export const previewEntriesKey = (entries: PreviewEntry[]): string => JSON.stringify(entries);

const cardActionLabels: { [action in CardAction]: TMessageKey } = {
  EXPAND: "enterprise-account.dashboards.action.expand",
};

const cardActionIcons: { [action in CardAction]: IconProps["name"] } = {
  EXPAND: "corners-out",
};

const templateCardActionLabels: { [action in TemplateCardAction]: TMessageKey } = {
  DOWNLOAD: "download",
};

const templateCardActionIcons: { [action in TemplateCardAction]: IconProps["name"] } = {
  DOWNLOAD: "download",
};

export const cardActionLabel = (action: CardAction | TemplateCardAction): TMessageKey =>
  action === "EXPAND" ? cardActionLabels[action] : templateCardActionLabels[action];

const chosenActions = (config: DashboardCardConfig): string[] => {
  switch (config.type) {
    case "ENTITY_DATA":
    case "COMPANY_ROLES":
    case "ROLE_HOLDER":
      return [];
    case "TEMPLATE":
      return config.actions ?? [];
  }
};

const actionButtons = <A extends string>(
  actions: readonly A[],
  chosen: string[],
  labels: { [action in A]: TMessageKey },
  icons: { [action in A]: IconProps["name"] },
  handlers: { [action in A]: () => void },
): LinkButtonProps[] =>
  actions
    .filter((action) => chosen.includes(action))
    .map((action) => ({
      label: { key: labels[action] },
      icon: icons[action],
      onClick: handlers[action],
    }));

export const cardButtons = (
  config: DashboardCardConfig,
  handlers: { [action in CardAction]: () => void },
): LinkButtonProps[] =>
  actionButtons(cardActions, chosenActions(config), cardActionLabels, cardActionIcons, handlers);

export const templateCardButtons = (
  config: DashboardCardConfig,
  handlers: { [action in TemplateCardAction]: () => void },
): LinkButtonProps[] =>
  actionButtons(
    templateCardActions,
    chosenActions(config),
    templateCardActionLabels,
    templateCardActionIcons,
    handlers,
  );

export const downloadGraphPng = (
  graphRef: MutableRefObject<GraphUtils | null>,
  fileName: string,
  dispatch: EventDispatcher,
): void =>
  void Promise.resolve(graphRef.current?.toPng() ?? null)
    .then((blob) =>
      blob
        ? downloadFile(blob, `${fileName}.png`)
        : Promise.reject(new Error("The graph produced no image")),
    )
    .catch((error) => {
      logError(error);
      dispatch([showGeneralError()]);
    });
